import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useQZPrint } from '../contexts/QZPrintContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SplitBillModal } from '../components/SplitBillModal';
import { VoidOrderModal } from '../components/VoidOrderModal';
import { toast } from 'sonner';
import {
  Search,
  Loader2,
  CreditCard,
  Banknote,
  QrCode,
  Smartphone,
  RefreshCw,
  Clock,
  ChefHat,
  CheckCircle,
  UtensilsCrossed,
  Split,
  Receipt,
  Wifi,
  WifiOff,
  Printer,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Ban,
  Trash2,
  ArrowRightLeft,
  Plus,
  Minus,
  Percent,
  Tag,
} from 'lucide-react';
import { OrderCardSkeleton } from '../components/ui/loading-skeletons';

// Status configuration will be translated dynamically
const getStatusConfig = (t) => ({
  CONFIRMED: { label: t('orders.status.confirmed'), icon: CheckCircle, color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30' },
  PREPARING: { label: t('orders.status.preparing'), icon: ChefHat, color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30' },
  READY: { label: t('orders.status.ready'), icon: Clock, color: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/30' },
  SERVED: { label: t('orders.status.served'), icon: UtensilsCrossed, color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30' },
  VOID: { label: t('orders.status.void'), icon: Ban, color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30' },
});

const ORDER_TYPE_COLORS = {
  DINE_IN: 'order-type-dinein',
  TAKEAWAY: 'order-type-takeaway',
  DELIVERY: 'order-type-delivery',
};

export default function Orders() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { currentOutlet } = useOutlet();
  const { formatCurrency } = useCurrency();
  const { isConnected: qzConnected, canPrintReceipt, printReceipt } = useQZPrint();
  
  // Initialize STATUS_CONFIG with translation function
  const STATUS_CONFIG = getStatusConfig(t);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeStatus, setActiveStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Payment Dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Split Bill Modal
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitOrder, setSplitOrder] = useState(null);
  
  // Void Order Modal
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidOrder, setVoidOrder] = useState(null);
  
  // Move Table Modal
  const [showMoveTableModal, setShowMoveTableModal] = useState(false);
  const [moveTableOrder, setMoveTableOrder] = useState(null);
  const [availableTables, setAvailableTables] = useState([]);
  const [selectedTargetTable, setSelectedTargetTable] = useState('');
  const [moveTableReason, setMoveTableReason] = useState('');
  const [movingTable, setMovingTable] = useState(false);
  
  // Add-On Order Modal
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [addOnOrder, setAddOnOrder] = useState(null);
  const [addOnItems, setAddOnItems] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addOnSearchQuery, setAddOnSearchQuery] = useState('');
  const [addOnActiveCategory, setAddOnActiveCategory] = useState('all');
  const [submittingAddOn, setSubmittingAddOn] = useState(false);
  const [showItemVoidDialog, setShowItemVoidDialog] = useState(false);
  const [voidItemTarget, setVoidItemTarget] = useState(null);
  const [voidItemReason, setVoidItemReason] = useState('');
  
  // Discount Modal
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountOrder, setDiscountOrder] = useState(null);
  const [discountItemId, setDiscountItemId] = useState(null);
  const [discountType, setDiscountType] = useState('PERCENT');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [discountPresets, setDiscountPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  
  // Print Jobs tracking
  const [printJobs, setPrintJobs] = useState({});  // orderId -> jobs array
  const [reprintingOrder, setReprintingOrder] = useState(null);
  const [printerStatus, setPrinterStatus] = useState({});  // printerId -> status info

  // Fetch printer status for offline warnings
  const fetchPrinterStatus = useCallback(async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/printers/status', {
        params: { outlet_id: currentOutlet.id },
      });
      const statusMap = {};
      resp.data.forEach(p => {
        statusMap[p.id] = {
          name: p.name,
          status: p.status || 'UNKNOWN',
          printer_role: p.printer_role,
        };
      });
      setPrinterStatus(statusMap);
    } catch (error) {
      console.error('Failed to fetch printer status:', error);
    }
  }, [api, currentOutlet?.id]);

  // Check if any printer routing to order items is offline
  const getOfflinePrinterWarning = (order) => {
    const offlinePrinters = Object.values(printerStatus).filter(
      p => p.status === 'OFFLINE'
    );
    if (offlinePrinters.length === 0) return null;
    return offlinePrinters.map(p => p.name).join(', ');
  };

  const fetchOrders = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      // Fetch orders that are not yet completed or cancelled
      const resp = await api.get('/orders', {
        params: { 
          outlet_id: currentOutlet.id,
        },
      });
      
      // Filter to show only active unpaid orders (postpaid)
      const activeOrders = resp.data.filter(order => 
        ['CONFIRMED', 'PREPARING', 'READY', 'SERVED'].includes(order.status) &&
        order.payment_mode === 'POSTPAID'
      );
      
      setOrders(activeOrders);
      
      // Fetch print jobs for these orders
      const jobsMap = {};
      for (const order of activeOrders) {
        try {
          const jobsResp = await api.get(`/orders/${order.id}/print-jobs`);
          jobsMap[order.id] = jobsResp.data;
        } catch (e) {
          // Ignore errors for individual order print jobs
        }
      }
      setPrintJobs(jobsMap);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [api, currentOutlet?.id]);

  // Get print status for an order
  const getPrintStatus = (orderId) => {
    const jobs = printJobs[orderId] || [];
    if (jobs.length === 0) return { status: 'none', label: t('orders.no_print'), color: 'text-muted-foreground' };
    
    const hasFailed = jobs.some(j => j.status === 'FAILED');
    const allPrinted = jobs.every(j => j.status === 'PRINTED');
    const hasPending = jobs.some(j => j.status === 'PENDING' || j.status === 'SENT');
    
    if (hasFailed) return { status: 'failed', label: t('orders.print_failed'), color: 'text-red-500', icon: XCircle };
    if (allPrinted) return { status: 'printed', label: t('orders.printed'), color: 'text-green-500', icon: CheckCircle2 };
    if (hasPending) return { status: 'pending', label: t('common.printing'), color: 'text-yellow-500', icon: AlertCircle };
    return { status: 'unknown', label: t('common.unknown'), color: 'text-muted-foreground', icon: Printer };
  };

  // Reprint order
  const handleReprint = async (orderId) => {
    setReprintingOrder(orderId);
    try {
      await api.post(`/orders/${orderId}/reprint`);
      toast.success('Print jobs created');
      // Refresh print jobs
      const jobsResp = await api.get(`/orders/${orderId}/print-jobs`);
      setPrintJobs(prev => ({ ...prev, [orderId]: jobsResp.data }));
    } catch (error) {
      toast.error('Failed to reprint');
    } finally {
      setReprintingOrder(null);
    }
  };

  // Retry failed print job
  const handleRetryPrint = async (orderId, jobId) => {
    try {
      await api.post(`/print-jobs/${jobId}/retry`);
      toast.success('Retry queued');
      // Refresh print jobs
      const jobsResp = await api.get(`/orders/${orderId}/print-jobs`);
      setPrintJobs(prev => ({ ...prev, [orderId]: jobsResp.data }));
    } catch (error) {
      toast.error('Failed to retry');
    }
  };

  // Void order handlers
  const openVoidModal = (order) => {
    setVoidOrder(order);
    setShowVoidModal(true);
  };

  const handleVoidSuccess = () => {
    fetchOrders();
    setVoidOrder(null);
  };

  useEffect(() => {
    fetchOrders();
    fetchPrinterStatus();
  }, [fetchOrders, fetchPrinterStatus]);

  // Auto-refresh printer status every 30 seconds
  useEffect(() => {
    if (!currentOutlet?.id) return;
    const interval = setInterval(fetchPrinterStatus, 30000);
    return () => clearInterval(interval);
  }, [currentOutlet?.id, fetchPrinterStatus]);

  // WebSocket for real-time updates
  const handleWebSocketMessage = useCallback((message) => {
    if (['order.created', 'order.updated', 'order.status_changed', 'order.split'].includes(message.event)) {
      fetchOrders();
    }
    // Listen for printer status changes
    if (message.event === 'printer.status_changed') {
      fetchPrinterStatus();
      // Show toast only when printer goes offline
      if (message.data?.new_status === 'OFFLINE') {
        toast.warning(`Printer ${message.data?.printer_name || ''} is OFFLINE`, {
          description: t('orders.printer_offline_warning'),
          duration: 5000,
        });
      }
    }
  }, [fetchOrders, fetchPrinterStatus]);

  const { isConnected } = useWebSocket(currentOutlet?.id, handleWebSocketMessage);

  const getTimeSince = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMinutes = Math.floor((now - created) / (1000 * 60));
    if (diffMinutes < 1) return t('common.just_now');
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const hours = Math.floor(diffMinutes / 60);
    const mins = diffMinutes % 60;
    return `${hours}h ${mins}m ago`;
  };

  const openPaymentDialog = (order) => {
    setSelectedOrder(order);
    setPaymentAmount(order.total.toString());
    setPaymentMethod('CASH');
    setShowPaymentDialog(true);
  };

  const openSplitModal = (order) => {
    setSplitOrder(order);
    setShowSplitModal(true);
  };

  const handlePayment = async () => {
    if (!selectedOrder) return;
    
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount < selectedOrder.total) {
      toast.error('Payment amount must be at least ' + formatCurrency(selectedOrder.total));
      return;
    }

    setProcessingPayment(true);
    try {
      const paymentResp = await api.post('/payments', {
        order_id: selectedOrder.id,
        outlet_id: currentOutlet.id,
        method: paymentMethod,
        amount: amount,
      });

      const change = amount - selectedOrder.total;
      
      toast.success(
        <div className="flex flex-col">
          <span className="font-bold">Payment successful!</span>
          <span className="text-sm">Order #{selectedOrder.order_number}</span>
          {change > 0 && (
            <span className="text-sm font-mono mt-1">Change: {formatCurrency(change)}</span>
          )}
        </div>
      );
      
      // Store order data for receipt printing
      const paidOrderId = selectedOrder.id;
      const paidOrderNumber = selectedOrder.order_number;
      const paidOrder = { ...selectedOrder };
      
      setShowPaymentDialog(false);
      setSelectedOrder(null);
      fetchOrders();
      
      // ===== AUTO PRINT RECEIPT AFTER PAYMENT =====
      if (qzConnected && canPrintReceipt) {
        try {
          // Add payment info to order for receipt
          const orderForReceipt = {
            ...paidOrder,
            payments: [{
              method: paymentMethod,
              amount: amount,
            }],
            cashier_name: paymentResp.data?.processed_by_name || null,
          };
          
          await printReceipt(orderForReceipt, currentOutlet);
          console.log('[Orders] Receipt auto-printed after payment');
        } catch (printErr) {
          console.error('[Orders] Auto-print failed:', printErr);
          // Show manual print option on error
          toast.error(
            <div className="flex flex-col gap-2">
              <span>Auto-print gagal: {printErr.message}</span>
              <button 
                className="px-3 py-1 bg-primary text-primary-foreground rounded text-sm"
                onClick={() => handlePrintReceipt(paidOrderId, paidOrderNumber)}
              >
                Coba Print Ulang
              </button>
            </div>,
            { duration: 15000 }
          );
        }
      } else {
        // QZ not connected - show error (NO FALLBACK for receipt)
        toast.warning(
          <div className="flex flex-col gap-2">
            <span>QZ Tray tidak terhubung - receipt tidak dicetak otomatis</span>
            <span className="text-xs opacity-70">Buka menu Printer untuk setup</span>
          </div>,
          { duration: 8000 }
        );
      }
      // ===== END AUTO PRINT =====
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Print receipt for completed/paid order
  const handlePrintReceipt = async (orderId, orderNumber) => {
    // First, try QZ Tray direct print
    if (qzConnected && canPrintReceipt) {
      try {
        // Fetch full order data for printing
        const orderResp = await api.get(`/orders/${orderId}`);
        const order = orderResp.data;
        
        await printReceipt(order, currentOutlet);
        toast.success(`Receipt printed for #${orderNumber}`);
        return;
      } catch (printErr) {
        console.error('[Orders] QZ Print failed:', printErr);
        toast.error(`Print failed: ${printErr.message}`);
        return; // NO FALLBACK for receipt - stop here
      }
    }
    
    // QZ not connected - show error (NO FALLBACK for receipt)
    toast.error('QZ Tray tidak terhubung. Receipt harus dicetak via QZ Tray.');
  };

  // Move Table functions
  const openMoveTableModal = async (order) => {
    setMoveTableOrder(order);
    setSelectedTargetTable('');
    setMoveTableReason('');
    setMovingTable(false);
    
    try {
      const resp = await api.get(`/orders/${order.id}/can-move-table`);
      if (resp.data.can_move) {
        setAvailableTables(resp.data.available_tables || []);
        setShowMoveTableModal(true);
      } else {
        toast.error(resp.data.reason || 'Cannot move this order');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check move eligibility');
    }
  };

  const handleMoveTable = async () => {
    if (!moveTableOrder || !selectedTargetTable) {
      toast.error('Please select a target table');
      return;
    }
    
    setMovingTable(true);
    try {
      const resp = await api.post(`/orders/${moveTableOrder.id}/move-table`, {
        to_table_id: selectedTargetTable,
        reason: moveTableReason || null,
      });
      
      toast.success(resp.data.message);
      setShowMoveTableModal(false);
      setMoveTableOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move table');
    } finally {
      setMovingTable(false);
    }
  };

  // Check if order can be moved (not PAID, VOID, COMPLETED, CANCELLED)
  const canMoveTable = (order) => {
    const eligibleStatuses = ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'SERVED'];
    return order.table_id && eligibleStatuses.includes(order.status);
  };

  // Add-On Order Functions
  const canAddOn = (order) => {
    const eligibleStatuses = ['CONFIRMED', 'PREPARING', 'READY', 'SERVED'];
    return eligibleStatuses.includes(order.status);
  };

  const fetchMenuForAddOn = async () => {
    if (!currentOutlet?.id) return;
    try {
      const [menuResp, catResp] = await Promise.all([
        api.get('/menu-items', { params: { outlet_id: currentOutlet.id } }),
        api.get('/categories', { params: { outlet_id: currentOutlet.id } }),
      ]);
      setMenuItems(menuResp.data.filter(m => m.is_available));
      setCategories(catResp.data);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
    }
  };

  const openAddOnModal = async (order) => {
    // Check eligibility first
    try {
      const resp = await api.get(`/orders/${order.id}/can-add-on`);
      if (!resp.data.can_add_on) {
        toast.error(resp.data.reason || 'Cannot add items to this order');
        return;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check add-on eligibility');
      return;
    }

    setAddOnOrder(order);
    setAddOnItems([]);
    setAddOnSearchQuery('');
    setAddOnActiveCategory('all');
    await fetchMenuForAddOn();
    setShowAddOnModal(true);
  };

  const addItemToAddOn = (menuItem) => {
    const existingIndex = addOnItems.findIndex(i => i.menu_item_id === menuItem.id);
    if (existingIndex >= 0) {
      const updated = [...addOnItems];
      updated[existingIndex].quantity += 1;
      setAddOnItems(updated);
    } else {
      setAddOnItems([...addOnItems, {
        menu_item_id: menuItem.id,
        menu_item_name: menuItem.name,
        quantity: 1,
        unit_price: menuItem.base_price,
        add_on_total: 0,
        kitchen_station: menuItem.kitchen_station,
      }]);
    }
  };

  const updateAddOnItemQty = (index, delta) => {
    const updated = [...addOnItems];
    updated[index].quantity += delta;
    if (updated[index].quantity <= 0) {
      updated.splice(index, 1);
    }
    setAddOnItems(updated);
  };

  const removeAddOnItem = (index) => {
    const updated = [...addOnItems];
    updated.splice(index, 1);
    setAddOnItems(updated);
  };

  const submitAddOn = async () => {
    if (!addOnOrder || addOnItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setSubmittingAddOn(true);
    try {
      const resp = await api.post(`/orders/${addOnOrder.id}/add-on`, {
        items: addOnItems,
      });
      
      toast.success(resp.data.message);
      setShowAddOnModal(false);
      setAddOnOrder(null);
      setAddOnItems([]);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add items');
    } finally {
      setSubmittingAddOn(false);
    }
  };

  const addOnSubtotal = addOnItems.reduce((sum, item) => sum + (item.unit_price * item.quantity) + (item.add_on_total || 0), 0);

  const filteredMenuItems = menuItems.filter(item => {
    const matchesCategory = addOnActiveCategory === 'all' || item.category_id === addOnActiveCategory;
    const matchesSearch = item.name.toLowerCase().includes(addOnSearchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Item-level void functions
  const openVoidItemDialog = (order, item) => {
    setVoidItemTarget({ order, item });
    setVoidItemReason('');
    setShowItemVoidDialog(true);
  };

  const handleVoidItem = async () => {
    if (!voidItemTarget || !voidItemReason.trim()) {
      toast.error('Please provide a reason for voiding');
      return;
    }

    try {
      const resp = await api.post(
        `/orders/${voidItemTarget.order.id}/items/${voidItemTarget.item.id}/void`,
        { reason: voidItemReason }
      );
      toast.success(resp.data.message);
      setShowItemVoidDialog(false);
      setVoidItemTarget(null);
      setVoidItemReason('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void item');
    }
  };

  const canVoidItem = (item) => {
    // Cannot void if already voided or SERVED
    if (item.voided_at) return false;
    if (item.kitchen_status === 'SERVED') return false;
    return true;
  };

  // Discount functions
  const fetchDiscountPresets = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoadingPresets(true);
    try {
      const resp = await api.get('/discount-presets', { params: { outlet_id: currentOutlet.id } });
      setDiscountPresets(resp.data);
    } catch (error) {
      console.error('Failed to fetch discount presets:', error);
      setDiscountPresets([]);
    } finally {
      setLoadingPresets(false);
    }
  }, [api, currentOutlet?.id]);

  const openDiscountModal = (order, itemId = null) => {
    // Check if order can be discounted
    if (['COMPLETED', 'CANCELLED', 'VOID'].includes(order.status)) {
      toast.error('Tidak dapat memberikan diskon pada order yang sudah selesai');
      return;
    }
    setDiscountOrder(order);
    setDiscountItemId(itemId);
    setDiscountType('PERCENT');
    setDiscountValue('');
    setDiscountReason('');
    setShowDiscountModal(true);
    fetchDiscountPresets();
  };

  const applyPreset = (preset) => {
    setDiscountType(preset.discount_type);
    setDiscountValue(preset.value.toString());
    setDiscountReason(preset.reason);
  };

  const canDiscountItem = (item) => {
    // Cannot discount SERVED or VOID items
    if (item.voided_at) return false;
    if (item.kitchen_status === 'SERVED') return false;
    if (item.kitchen_status === 'VOID') return false;
    return true;
  };

  const handleApplyDiscount = async () => {
    if (!discountOrder) return;
    if (!discountValue || parseFloat(discountValue) <= 0) {
      toast.error('Masukkan nilai diskon yang valid');
      return;
    }
    if (!discountReason.trim()) {
      toast.error('Alasan diskon wajib diisi');
      return;
    }

    setApplyingDiscount(true);
    try {
      const resp = await api.post('/discounts', {
        order_id: discountOrder.id,
        order_item_id: discountItemId || null,
        discount_type: discountType,
        value: parseFloat(discountValue),
        reason: discountReason.trim(),
      });
      toast.success(resp.data.message || 'Diskon berhasil diterapkan');
      setShowDiscountModal(false);
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Gagal menerapkan diskon');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const getDiscountLabel = () => {
    if (discountItemId && discountOrder) {
      const item = discountOrder.items?.find(i => i.id === discountItemId);
      return item ? t('orders.item_discount', { name: item.menu_item_name }) : t('orders.item_discount', { name: '' });
    }
    return t('orders.order_discount');
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = activeStatus === 'all' || order.status === activeStatus;
    const matchesSearch = 
      order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.table_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Count orders by status
  const statusCounts = {
    all: orders.length,
    CONFIRMED: orders.filter(o => o.status === 'CONFIRMED').length,
    PREPARING: orders.filter(o => o.status === 'PREPARING').length,
    READY: orders.filter(o => o.status === 'READY').length,
    SERVED: orders.filter(o => o.status === 'SERVED').length,
  };

  if (!currentOutlet) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-muted-foreground">Please select an outlet first</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="orders-page">
      {/* Printer Offline Warning Banner */}
      {(() => {
        const offlinePrinters = Object.values(printerStatus).filter(p => p.status === 'OFFLINE');
        if (offlinePrinters.length === 0) return null;
        
        return (
          <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4 flex items-start gap-3" data-testid="printer-offline-warning">
            <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-500">Printer Offline Warning</p>
              <p className="text-sm text-muted-foreground">
                {offlinePrinters.map(p => p.name).join(', ')} {offlinePrinters.length > 1 ? 'are' : 'is'} offline. 
                Tickets will not print automatically until the printer is back online.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6" />
            Active Orders
          </h1>
          <p className="text-muted-foreground">Manage unpaid orders and process payments</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isConnected ? 'default' : 'destructive'} className="gap-1">
            {isConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {isConnected ? t('common.live') : t('common.offline')}
          </Badge>
          <Button variant="outline" onClick={fetchOrders} disabled={loading} data-testid="refresh-orders">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('orders.search_placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="order-search"
        />
      </div>

      {/* Status Tabs */}
      <Tabs value={activeStatus} onValueChange={setActiveStatus}>
        <TabsList>
          <TabsTrigger value="all" className="gap-2">
            All
            <Badge variant="secondary" className="ml-1">{statusCounts.all}</Badge>
          </TabsTrigger>
          <TabsTrigger value="SERVED" className="gap-2">
            <UtensilsCrossed className="h-4 w-4" />
            Ready to Pay
            <Badge variant="secondary" className="ml-1 bg-purple-500/20">{statusCounts.SERVED}</Badge>
          </TabsTrigger>
          <TabsTrigger value="READY" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Ready
            <Badge variant="secondary" className="ml-1">{statusCounts.READY}</Badge>
          </TabsTrigger>
          <TabsTrigger value="PREPARING" className="gap-2">
            <ChefHat className="h-4 w-4" />
            Preparing
            <Badge variant="secondary" className="ml-1">{statusCounts.PREPARING}</Badge>
          </TabsTrigger>
          <TabsTrigger value="CONFIRMED" className="gap-2">
            <Clock className="h-4 w-4" />
            Confirmed
            <Badge variant="secondary" className="ml-1">{statusCounts.CONFIRMED}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeStatus} className="mt-4">
          {loading ? (
            <OrderCardSkeleton count={6} />
          ) : filteredOrders.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-64">
                <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No active orders</p>
                <p className="text-sm text-muted-foreground">Postpaid orders will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredOrders.map((order) => {
                const StatusIcon = STATUS_CONFIG[order.status]?.icon || Clock;
                const isServed = order.status === 'SERVED';
                
                return (
                  <Card 
                    key={order.id} 
                    className={`${isServed ? 'border-purple-500 ring-2 ring-purple-500/20' : ''}`}
                    data-testid={`order-card-${order.order_number}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl font-mono">
                            #{order.order_number}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={ORDER_TYPE_COLORS[order.order_type]}>
                              {order.order_type.replace('_', ' ')}
                            </Badge>
                            {order.table_number && (
                              <Badge variant="secondary">{order.table_number}</Badge>
                            )}
                            {order.is_qr_order && (
                              <Badge className="bg-purple-600 text-white">QR</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className={STATUS_CONFIG[order.status]?.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {STATUS_CONFIG[order.status]?.label}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {getTimeSince(order.created_at)}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Items Summary with discount info */}
                      <div className="text-sm space-y-1 max-h-28 overflow-y-auto">
                        {order.items?.slice(0, 4).map((item, idx) => (
                          <div key={idx} className="flex justify-between items-start">
                            <span className={`text-muted-foreground ${item.voided_at ? 'line-through' : ''}`}>
                              {item.quantity}x {item.menu_item_name}
                            </span>
                            {item.discount_amount > 0 && !item.voided_at && (
                              <span className="text-xs text-orange-500 flex items-center gap-0.5">
                                <Tag className="h-2.5 w-2.5" />
                                -{formatCurrency(item.discount_amount)}
                              </span>
                            )}
                          </div>
                        ))}
                        {order.items?.length > 4 && (
                          <p className="text-xs text-muted-foreground">
                            +{order.items.length - 4} more items
                          </p>
                        )}
                      </div>

                      {/* Customer Info */}
                      {order.customer_name && (
                        <p className="text-sm text-muted-foreground">
                          Customer: {order.customer_name}
                        </p>
                      )}

                      {/* Print Status */}
                      {(() => {
                        const printStatus = getPrintStatus(order.id);
                        const PrintIcon = printStatus.icon || Printer;
                        const jobs = printJobs[order.id] || [];
                        const failedJobs = jobs.filter(j => j.status === 'FAILED');
                        
                        return (
                          <div className="flex items-center justify-between text-sm">
                            <div className={`flex items-center gap-1 ${printStatus.color}`}>
                              <PrintIcon className="h-4 w-4" />
                              <span>{printStatus.label}</span>
                              {jobs.length > 0 && (
                                <span className="text-xs text-muted-foreground">
                                  ({jobs.length} job{jobs.length > 1 ? 's' : ''})
                                </span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              {failedJobs.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => failedJobs.forEach(j => handleRetryPrint(order.id, j.id))}
                                  data-testid={`retry-print-${order.order_number}`}
                                >
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                  Retry
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => handleReprint(order.id)}
                                disabled={reprintingOrder === order.id}
                                data-testid={`reprint-${order.order_number}`}
                              >
                                <Printer className="h-3 w-3 mr-1" />
                                {reprintingOrder === order.id ? t('common.printing') : t('common.reprint')}
                              </Button>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Order Summary with Discount */}
                      <div className="space-y-1 pt-2 border-t text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span className="font-mono">{formatCurrency(order.subtotal || 0)}</span>
                        </div>
                        {(order.discount > 0) && (
                          <div className="flex justify-between text-orange-600">
                            <span className="flex items-center gap-1">
                              <Tag className="h-3 w-3" />
                              Diskon
                            </span>
                            <span className="font-mono">-{formatCurrency(order.discount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tax (10%)</span>
                          <span className="font-mono">{formatCurrency(order.tax || 0)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-1 border-t">
                          <span className="font-medium">Total</span>
                          <span className="text-xl font-bold font-mono text-primary">
                            {formatCurrency(order.total)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        {!order.is_split ? (
                          <>
                            <Button
                              className="flex-1"
                              onClick={() => openPaymentDialog(order)}
                              data-testid={`pay-btn-${order.order_number}`}
                            >
                              <CreditCard className="h-4 w-4 mr-2" />
                              Pay Bill
                            </Button>
                            {/* Add-On Button */}
                            {canAddOn(order) && (
                              <Button
                                variant="outline"
                                onClick={() => openAddOnModal(order)}
                                data-testid={`addon-btn-${order.order_number}`}
                                title={t('common.add_item')}
                                className="text-green-600 hover:text-green-700 hover:bg-green-500/10"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Discount Button */}
                            {!['COMPLETED', 'CANCELLED', 'VOID'].includes(order.status) && (
                              <Button
                                variant="outline"
                                onClick={() => openDiscountModal(order)}
                                data-testid={`discount-btn-${order.order_number}`}
                                title={t('common.discount')}
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-500/10"
                              >
                                <Percent className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              onClick={() => openSplitModal(order)}
                              data-testid={`split-btn-${order.order_number}`}
                            >
                              <Split className="h-4 w-4" />
                            </Button>
                            {/* Move Table Button - only for DINE_IN orders */}
                            {canMoveTable(order) && (
                              <Button
                                variant="outline"
                                onClick={() => openMoveTableModal(order)}
                                data-testid={`move-table-btn-${order.order_number}`}
                                title={t('common.move_table')}
                              >
                                <ArrowRightLeft className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                              onClick={() => openVoidModal(order)}
                              data-testid={`void-btn-${order.order_number}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            className="flex-1"
                            variant="secondary"
                            onClick={() => openSplitModal(order)}
                            data-testid={`view-splits-btn-${order.order_number}`}
                          >
                            <Split className="h-4 w-4 mr-2" />
                            View Split Bills
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Payment</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Summary */}
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Order</span>
                  <span className="font-mono font-medium">#{selectedOrder.order_number}</span>
                </div>
                {selectedOrder.table_number && (
                  <div className="flex justify-between mb-2">
                    <span className="text-muted-foreground">Table</span>
                    <span>{selectedOrder.table_number}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total</span>
                  <span className="font-mono text-primary">{formatCurrency(selectedOrder.total)}</span>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="text-sm font-medium mb-2 block">Payment Method</label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={paymentMethod === 'CASH' ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod('CASH')}
                    data-testid="payment-cash"
                  >
                    <Banknote className="h-4 w-4 mr-2" />
                    Cash
                  </Button>
                  <Button
                    variant={paymentMethod === 'QRIS' ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod('QRIS')}
                    data-testid="payment-qris"
                  >
                    <QrCode className="h-4 w-4 mr-2" />
                    QRIS
                  </Button>
                  <Button
                    variant={paymentMethod === 'BANK_TRANSFER' ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod('BANK_TRANSFER')}
                    data-testid="payment-transfer"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Transfer
                  </Button>
                  <Button
                    variant={paymentMethod === 'E_WALLET' ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod('E_WALLET')}
                    data-testid="payment-ewallet"
                  >
                    <Smartphone className="h-4 w-4 mr-2" />
                    E-Wallet
                  </Button>
                </div>
              </div>

              {/* Amount */}
              {paymentMethod === 'CASH' && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Amount Received</label>
                  <Input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="font-mono text-lg"
                    data-testid="payment-amount-input"
                  />
                  {parseFloat(paymentAmount) >= selectedOrder.total && (
                    <p className="text-sm text-green-600 mt-2 font-medium">
                      Change: {formatCurrency(parseFloat(paymentAmount) - selectedOrder.total)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button
              onClick={handlePayment}
              disabled={processingPayment || parseFloat(paymentAmount) < (selectedOrder?.total || 0)}
              data-testid="confirm-payment-btn"
            >
              {processingPayment && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Complete Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Bill Modal */}
      <SplitBillModal
        open={showSplitModal}
        onOpenChange={setShowSplitModal}
        order={splitOrder}
        onSuccess={() => {
          fetchOrders();
          setShowSplitModal(false);
        }}
      />

      {/* Void Order Modal */}
      <VoidOrderModal
        open={showVoidModal}
        onOpenChange={setShowVoidModal}
        order={voidOrder}
        api={api}
        onVoidSuccess={handleVoidSuccess}
      />

      {/* Move Table Modal */}
      <Dialog open={showMoveTableModal} onOpenChange={setShowMoveTableModal}>
        <DialogContent className="max-w-md" data-testid="move-table-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-blue-500" />
              Move Table
            </DialogTitle>
            <DialogDescription>
              Move order #{moveTableOrder?.order_number} to a different table
            </DialogDescription>
          </DialogHeader>
          
          {moveTableOrder && (
            <div className="space-y-4">
              {/* Current Table (Read-only) */}
              <div>
                <Label className="text-sm text-muted-foreground">From Table</Label>
                <div className="flex items-center gap-2 p-3 bg-muted rounded-md mt-1">
                  <Badge variant="secondary">{moveTableOrder.table_number}</Badge>
                  <span className="text-sm text-muted-foreground">Current Table</span>
                </div>
              </div>
              
              {/* Target Table Selection */}
              <div>
                <Label htmlFor="target-table">Move To Table</Label>
                <Select
                  value={selectedTargetTable}
                  onValueChange={setSelectedTargetTable}
                >
                  <SelectTrigger className="w-full mt-1" data-testid="target-table-select">
                    <SelectValue placeholder={t('common.select_target_table')} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTables.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">No available tables</div>
                    ) : (
                      availableTables.map(table => (
                        <SelectItem key={table.id} value={table.id}>
                          {table.number} (Capacity: {table.capacity})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {availableTables.length === 0 && (
                  <p className="text-xs text-amber-500 mt-1">All other tables are currently occupied</p>
                )}
              </div>
              
              {/* Optional Reason */}
              <div>
                <Label htmlFor="move-reason">Reason (Optional)</Label>
                <Textarea
                  id="move-reason"
                  value={moveTableReason}
                  onChange={(e) => setMoveTableReason(e.target.value)}
                  placeholder="e.g., Customer requested, table issue..."
                  className="mt-1"
                  rows={2}
                  data-testid="move-reason-input"
                />
              </div>
              
              {/* Info Notice */}
              <div className="text-xs text-muted-foreground bg-blue-500/10 p-2 rounded">
                <p>This will move the order to the new table without:</p>
                <ul className="list-disc list-inside mt-1">
                  <li>Creating a new order</li>
                  <li>Reprinting kitchen/bar tickets</li>
                  <li>Affecting payment or totals</li>
                </ul>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMoveTableModal(false)}
              disabled={movingTable}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMoveTable}
              disabled={movingTable || !selectedTargetTable}
              data-testid="confirm-move-table-btn"
            >
              {movingTable && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Move Table
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add-On Order Modal */}
      <Dialog open={showAddOnModal} onOpenChange={setShowAddOnModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="addon-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-500" />
              Add Items to Order #{addOnOrder?.order_number}
            </DialogTitle>
            <DialogDescription>
              Add new items to this order. Existing items cannot be modified.
            </DialogDescription>
          </DialogHeader>
          
          {/* Add-On Mode Banner */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-amber-600 dark:text-amber-400">
              <strong>Add-On Mode:</strong> Existing SERVED items cannot be modified or removed
            </span>
          </div>
          
          <div className="flex flex-1 gap-4 overflow-hidden">
            {/* Menu Items */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Search */}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                placeholder={t('pos.search_menu')}
                  value={addOnSearchQuery}
                  onChange={(e) => setAddOnSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="addon-search-input"
                />
              </div>
              
              {/* Categories */}
              <div className="flex gap-1 mb-2 overflow-x-auto pb-1">
                <Button
                  variant={addOnActiveCategory === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setAddOnActiveCategory('all')}
                >
                  All
                </Button>
                {categories.map(cat => (
                  <Button
                    key={cat.id}
                    variant={addOnActiveCategory === cat.id ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setAddOnActiveCategory(cat.id)}
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>
              
              {/* Menu Grid */}
              <ScrollArea className="flex-1">
                <div className="grid grid-cols-2 gap-2 pr-2">
                  {filteredMenuItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => addItemToAddOn(item)}
                      className="text-left p-3 rounded-lg border hover:bg-accent transition-colors"
                      data-testid={`addon-menu-${item.id}`}
                    >
                      <p className="font-medium text-sm line-clamp-1">{item.name}</p>
                      <p className="text-sm text-primary font-mono">{formatCurrency(item.base_price)}</p>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
            
            {/* Cart / New Items */}
            <div className="w-72 flex flex-col border-l pl-4">
              <h4 className="font-medium mb-2">New Items (Batch #{(addOnOrder?.last_batch_no || 1) + 1})</h4>
              
              <ScrollArea className="flex-1">
                {addOnItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Click items to add
                  </p>
                ) : (
                  <div className="space-y-2 pr-2">
                    {addOnItems.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-muted rounded">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.menu_item_name}</p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {formatCurrency(item.unit_price)} x {item.quantity}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => updateAddOnItemQty(idx, -1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 text-center text-sm">{item.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => updateAddOnItemQty(idx, 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500"
                            onClick={() => removeAddOnItem(idx)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              
              {addOnItems.length > 0 && (
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Subtotal:</span>
                    <span className="font-mono font-medium">{formatCurrency(addOnSubtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>New Total:</span>
                    <span className="font-mono font-bold text-primary">
                      {formatCurrency((addOnOrder?.total || 0) + addOnSubtotal + (addOnSubtotal * 0.1))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Existing Items (Read-only) */}
          {addOnOrder?.items && addOnOrder.items.length > 0 && (
            <div className="border-t pt-3 mt-3">
              <h4 className="font-medium text-sm mb-2 text-muted-foreground">
                Existing Items ({addOnOrder.items.filter(i => !i.voided_at).length} items)
              </h4>
              <div className="flex flex-wrap gap-2">
                {addOnOrder.items.filter(i => !i.voided_at).slice(0, 5).map((item, idx) => (
                  <Badge 
                    key={idx} 
                    variant="secondary"
                    className={item.kitchen_status === 'SERVED' ? 'bg-purple-500/20' : ''}
                  >
                    {item.quantity}x {item.menu_item_name}
                    {item.kitchen_status === 'SERVED' && ' (Served)'}
                  </Badge>
                ))}
                {addOnOrder.items.filter(i => !i.voided_at).length > 5 && (
                  <Badge variant="outline">
                    +{addOnOrder.items.filter(i => !i.voided_at).length - 5} more
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOnModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitAddOn}
              disabled={submittingAddOn || addOnItems.length === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-addon-btn"
            >
              {submittingAddOn && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Add-On ({addOnItems.length} items)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Void Dialog */}
      <Dialog open={showItemVoidDialog} onOpenChange={setShowItemVoidDialog}>
        <DialogContent className="max-w-md" data-testid="void-item-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Trash2 className="h-5 w-5" />
              Void Item
            </DialogTitle>
            <DialogDescription>
              Void "{voidItemTarget?.item?.menu_item_name}" from order #{voidItemTarget?.order?.order_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Reason (Required)</Label>
              <Textarea
                value={voidItemReason}
                onChange={(e) => setVoidItemReason(e.target.value)}
                placeholder={t('orders.void_item_placeholder')}
                rows={2}
                data-testid="void-item-reason"
              />
            </div>
            
            <div className="text-xs text-muted-foreground bg-red-500/10 p-2 rounded">
              <p>This action will:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Mark this item as VOID</li>
                <li>Update order total</li>
                <li>Create audit log</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemVoidDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleVoidItem}
              disabled={!voidItemReason.trim()}
              data-testid="confirm-void-item-btn"
            >
              Void Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="max-w-md" data-testid="discount-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Percent className="h-5 w-5" />
              {getDiscountLabel()}
            </DialogTitle>
            <DialogDescription>
              Order #{discountOrder?.order_number}
              {discountItemId && discountOrder?.items && (
                <span className="block text-xs mt-1">
                  Harga item: {formatCurrency(
                    (discountOrder.items.find(i => i.id === discountItemId)?.unit_price || 0) *
                    (discountOrder.items.find(i => i.id === discountItemId)?.quantity || 1)
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Quick Presets */}
            {discountPresets.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground">Diskon Cepat</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {loadingPresets ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    discountPresets.map((preset) => {
                      const colorClasses = {
                        blue: 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-700 border-blue-500/30',
                        orange: 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-700 border-orange-500/30',
                        purple: 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-700 border-purple-500/30',
                        red: 'bg-red-500/10 hover:bg-red-500/20 text-red-700 border-red-500/30',
                        green: 'bg-green-500/10 hover:bg-green-500/20 text-green-700 border-green-500/30',
                      };
                      const colorClass = colorClasses[preset.color] || 'bg-gray-500/10 hover:bg-gray-500/20 text-gray-700 border-gray-500/30';
                      return (
                        <Button
                          key={preset.id}
                          variant="outline"
                          size="sm"
                          onClick={() => applyPreset(preset)}
                          className={`${colorClass} border text-xs font-medium`}
                          data-testid={`preset-${preset.id}`}
                        >
                          <Tag className="h-3 w-3 mr-1" />
                          {preset.name}
                        </Button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Discount Type */}
            <div>
              <Label>Tipe Diskon</Label>
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger data-testid="discount-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERCENT">Persen (%)</SelectItem>
                  <SelectItem value="FIXED">Nominal (Rp)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Discount Value */}
            <div>
              <Label>Nilai Diskon {discountType === 'PERCENT' ? '(%)' : '(Rp)'}</Label>
              <Input
                type="number"
                min="0"
                max={discountType === 'PERCENT' ? 100 : undefined}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === 'PERCENT' ? 'Contoh: 10' : 'Contoh: 5000'}
                data-testid="discount-value-input"
              />
            </div>
            
            {/* Reason (Required) */}
            <div>
              <Label>Alasan Diskon (Wajib)</Label>
              <Textarea
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                placeholder={t('orders.discount_reason_placeholder')}
                rows={2}
                data-testid="discount-reason-input"
              />
            </div>
            
            {/* Preview */}
            {discountValue && parseFloat(discountValue) > 0 && (
              <div className="text-sm bg-orange-500/10 p-3 rounded border border-orange-500/20">
                <p className="font-medium text-orange-700">Preview Diskon:</p>
                <p className="text-orange-600">
                  {discountType === 'PERCENT' 
                    ? `${discountValue}% dari ${discountItemId ? 'harga item' : 'subtotal order'}`
                    : formatCurrency(parseInt(discountValue) || 0)
                  }
                </p>
              </div>
            )}
            
            <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
              <p>Catatan:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Diskon tidak dapat diedit setelah diterapkan</li>
                <li>Diskon akan tercatat di audit log</li>
                <li>Diskon ditampilkan di struk</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountModal(false)}>
              Batal
            </Button>
            <Button
              onClick={handleApplyDiscount}
              disabled={applyingDiscount || !discountValue || !discountReason.trim()}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="apply-discount-btn"
            >
              {applyingDiscount ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Memproses...
                </>
              ) : (
                <>
                  <Tag className="h-4 w-4 mr-2" />
                  Terapkan Diskon
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
